import React, { Component } from 'react';
import {
  arrayOf,
  string,
  objectOf,
  array,
  number,
  bool,
  func,
} from 'prop-types';
import { desaturate, mix } from 'polished';
import {
  FlexibleWidthXYPlot,
  XAxis,
  YAxis,
  HorizontalGridLines,
  VerticalGridLines,
  LineSeries,
  MarkSeries,
} from 'react-vis';

import { withGroupContext } from 'context/groupContext';
import colors from 'styles/theme/colors';

import LineChartTooltip from './LineChartTooltip/LineChartTooltip';

import 'react-vis/dist/style.css';
import {
  axes,
  gridLines,
  defaultLineSeries,
  defaultMarkSeries,
} from './LineChart.style';

const mapData = (data, id, xKey, yKey) => {
  return (
    data &&
    data.map(item => {
      return {
        ...item,
        id,
        x: new Date(item[xKey] * 1000),
        y: item[yKey],
      };
    })
  );
};

class LineChart extends Component {
  constructor(props) {
    super(props);

    const margin = {
      top: 10,
      right: 60,
      bottom: 50,
      left: 0,
    };
    const size = {
      height: 400,
    };

    this.state = {
      size,
      margin,
      hoveredNode: null,
      hoveredSeries: null,
    };
  }

  handleMouseLeave = () => {
    this.setState({ hoveredNode: null, hoveredSeries: null });
  };

  handleHover = (id, value) => {
    if (this.state.hoveredSeries && id === this.state.hoveredSeries.id) {
      this.setState({ hoveredNode: value });
    }
  };

  handleSeriesMouseOver = (id, index) => {
    this.setState({ hoveredSeries: { id, index } });
  };

  handleClick = node => {
    /**
     * If one of the line series is active (a.k. hovered) only then
     * call the function that handles the node click.
     */
    if (this.state.hoveredSeries) {
      this.props.onNodeClick(node);
    }
  };

  render() {
    const {
      groupsWithData,
      startDate,
      endDate,
      scores,
      colorById,
    } = this.props;
    const { size, margin, hoveredNode, hoveredSeries } = this.state;

    return (
      <FlexibleWidthXYPlot
        id="chart-container"
        xType="time-utc"
        xDomain={[startDate, endDate]}
        height={size.height}
        margin={margin}
        onMouseLeave={this.handleMouseLeave}
        onClick={() => this.handleClick(hoveredNode)}
      >
        <HorizontalGridLines style={gridLines} />
        <VerticalGridLines tickTotal={8} style={gridLines} />
        <XAxis
          attr="x"
          attrAxis="x"
          orientation="bottom"
          position="start"
          tickTotal={8}
          tickSize={0}
          style={axes}
        />
        <YAxis
          attr="y"
          attrAxis="y"
          orientation="right"
		  yDomain={[0, 200]}
          tickSize={0}
          style={axes}
        />
        {groupsWithData &&
          groupsWithData.map((key, index) => {
            // Hide reference if needed
            if (!this.props.showReference && key === this.props.referenceId)
              return null;

            // Define default line color
            let stroke = colorById[key];

            /**
             * When line is hovered and key does not match line id desaturate
             * line and make it less less visible.
             */
            if (hoveredSeries && key !== hoveredSeries.id) {
              stroke = mix(0.8, colors.base.Smoke, desaturate(0.8, stroke));
            }

            return (
              <LineSeries
                key={`line-${key}`}
                curve="curveMonotoneX"
                data={mapData(scores[key], key, 'date', 'score')}
                onSeriesMouseOver={() => this.handleSeriesMouseOver(key, index)}
                onNearestX={value => this.handleHover(key, value)}
                style={{
                  ...defaultLineSeries,
                  strokeDasharray:
                    key === this.props.referenceId ? '1, 4' : null,
                  stroke,
                  strokeWidth: 3,
                  pointerEvents: 'stroke',
                  transition: 'stroke 150ms ease-in-out',
                }}
              />
            );
          })}
        {groupsWithData &&
          groupsWithData.map((key, index) => {
            // Hide reference if needed
            if (!this.props.showReference && key === this.props.referenceId)
              return null;

            // Define default line color
            let stroke = colorById[key];
            let fill = colors.base.DarkBlue;

            /**
             * When line is hovered and key does not match line id desaturate
             * line and make it less less visible.
             */
            if (hoveredSeries && key !== hoveredSeries.id) {
              stroke = mix(0.8, colors.base.Smoke, desaturate(0.8, stroke));
              fill = mix(0.8, colors.base.Smoke, desaturate(0.8, fill));
            }

            return (
              <MarkSeries
                key={`mark-${key}`}
                data={mapData(scores[key], key, 'date', 'score')}
                getNull={d => d.annotations.length >= 1}
                strokeWidth={5}
                size={4}
                style={{ ...defaultMarkSeries, stroke, fill }}
              />
            );
          })}
        {hoveredNode && (
          <LineChartTooltip
            value={hoveredNode}
            color={colorById[hoveredSeries.id]}
          />
        )}
      </FlexibleWidthXYPlot>
    );
  }
}

LineChart.propTypes = {
  referenceId: string.isRequired,
  groupsWithData: arrayOf(string).isRequired,
  colorById: objectOf(string).isRequired,
  scores: objectOf(array),
  startDate: number.isRequired,
  endDate: number.isRequired,
  onNodeClick: func.isRequired,
  showReference: bool,
};

export default withGroupContext(LineChart);



// WEBPACK FOOTER //
// ./src/components/LineChart/LineChart.js